import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtINR } from "@/lib/format";

export function linesOf(p) {
  if (p.line_items?.length) return p.line_items;
  return [{ line_no: 10, material_code: "", description: p.item, qty: p.quantity, uom: p.uom || "EA", unit_price: null, line_value: p.value }];
}

const TH = "border-b px-4 py-3 font-medium text-left text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap";
const TD = "border-b px-4 py-3 whitespace-nowrap";

/**
 * Enterprise PO line-item table: sticky header, horizontal scroll, sticky PO column,
 * PO number shown once per line-item group, truncated descriptions with tooltip.
 */
export default function POLineItemTable({ pos, showValue = true, showBuyer = false, linkBase = "/buyer/pos", testPrefix = "po-table", rowTestPrefix = "po-row" }) {
  if (!pos?.length) {
    return (
      <div className="rounded-lg border bg-card p-10 text-center text-sm text-muted-foreground" data-testid={testPrefix}>
        No purchase orders match.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border bg-card" data-testid={testPrefix}>
      <table className="w-full min-w-[1000px] border-collapse text-sm">
        <thead className="sticky top-0 z-20">
          <tr className="bg-white shadow-[0_1px_0_0_hsl(var(--border))]">
            <th className={`${TH} sticky left-0 z-30 bg-white`}>PO</th>
            {showBuyer && <th className={TH}>Buyer</th>}
            <th className={TH}>Line Item</th>
            <th className={TH}>Material Code</th>
            <th className={`${TH} min-w-72`}>Description</th>
            <th className={`${TH} text-right`}>Qty</th>
            <th className={TH}>UOM</th>
            {showValue && <th className={`${TH} text-right`}>Value</th>}
            <th className={TH}>Expected</th>
            <th className={TH}>Status</th>
          </tr>
        </thead>
        <tbody>
          {pos.map((p) =>
            linesOf(p).map((li, idx) => {
              const first = idx === 0;
              const groupCls = first ? "border-t-2 border-t-slate-200" : "";
              return (
                <tr key={`${p.id}-${li.line_no}`} className="group transition-colors hover:bg-slate-50">
                  <td className={`${TD} ${groupCls} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono font-semibold`}>
                    {first && (
                      <Link data-testid={`${rowTestPrefix}-${p.po_number}`} to={`${linkBase}/${p.id}`} className="text-slate-900 hover:underline">
                        {p.po_number}
                      </Link>
                    )}
                  </td>
                  {showBuyer && (
                    <td className={`${TD} ${groupCls} text-muted-foreground`}>{first ? (p.buyer_org_name || p.supplier_name) : ""}</td>
                  )}
                  <td className={`${TD} ${groupCls} font-mono text-muted-foreground`}>{li.line_no}</td>
                  <td className={`${TD} ${groupCls} font-mono text-xs`}>{li.material_code || "—"}</td>
                  <td className={`${TD} ${groupCls} max-w-80`}>
                    <span className="block truncate" title={li.description}>{li.description}</span>
                  </td>
                  <td className={`${TD} ${groupCls} text-right font-mono`}>{li.qty}</td>
                  <td className={`${TD} ${groupCls} text-muted-foreground`}>{li.uom}</td>
                  {showValue && <td className={`${TD} ${groupCls} text-right font-mono`}>{fmtINR(li.line_value)}</td>}
                  <td className={`${TD} ${groupCls}`}>
                    {first && (
                      <>
                        {fmtDate(p.expected_delivery_date)}
                        {p.delay_days > 0 && <span className="ml-1.5 text-xs font-semibold text-[#DC2626]">+{p.delay_days}d</span>}
                      </>
                    )}
                  </td>
                  <td className={`${TD} ${groupCls}`}>{first && <StatusBadge status={p.status} />}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Full-width line-item detail table for the PO detail page (unit price + line value). */
export function POLineItemsDetail({ po, testId = "po-line-items" }) {
  const lines = linesOf(po);
  const total = lines.reduce((a, li) => a + (li.line_value || 0), 0);
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="border-b px-5 py-2.5 font-medium">Line Item</th>
            <th className="border-b px-5 py-2.5 font-medium">Material Code</th>
            <th className="border-b px-5 py-2.5 font-medium min-w-72">Description</th>
            <th className="border-b px-5 py-2.5 font-medium text-right">Quantity</th>
            <th className="border-b px-5 py-2.5 font-medium">UOM</th>
            <th className="border-b px-5 py-2.5 font-medium text-right">Unit Price</th>
            <th className="border-b px-5 py-2.5 font-medium text-right">Line Value</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((li) => (
            <tr key={li.line_no} data-testid={`${testId}-line-${li.line_no}`} className="transition-colors hover:bg-slate-50">
              <td className="border-b px-5 py-3 font-mono font-medium">{li.line_no}</td>
              <td className="border-b px-5 py-3 font-mono text-xs">{li.material_code || "—"}</td>
              <td className="border-b px-5 py-3 whitespace-normal">{li.description}</td>
              <td className="border-b px-5 py-3 text-right font-mono">{li.qty}</td>
              <td className="border-b px-5 py-3 text-muted-foreground">{li.uom}</td>
              <td className="border-b px-5 py-3 text-right font-mono">{li.unit_price != null ? fmtINR(li.unit_price) : "—"}</td>
              <td className="border-b px-5 py-3 text-right font-mono font-medium">{fmtINR(li.line_value)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50">
            <td colSpan={6} className="px-5 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">PO Total</td>
            <td className="px-5 py-2.5 text-right font-mono font-semibold">{fmtINR(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
